import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";

import { AuthProvider } from "./lib/auth";
import { FlagsProvider } from "./lib/flags";
import Header from "./components/Header";
import Footer from "./components/Footer";

import Home from "./pages/Home";
import Destinations from "./pages/Destinations";
import DestinationDetail from "./pages/DestinationDetail";
import Adresses from "./pages/Adresses";
import AdresseDetail from "./pages/AdresseDetail";
import Galerie from "./pages/Galerie";
import Evenements from "./pages/Evenements";
import EvenementDetail from "./pages/EvenementDetail";
import Balades from "./pages/Balades";
import BaladeDetail from "./pages/BaladeDetail";
import Magazine from "./pages/Magazine";
import ArticleDetail from "./pages/ArticleDetail";
import Carte from "./pages/Carte";
import WeekendBuilder from "./pages/WeekendBuilder";
import Festival from "./pages/Festival";
import Experiences from "./pages/Experiences";
import ExperienceDetail from "./pages/ExperienceDetail";
import Parcours from "./pages/Parcours";
import ParcoursDetail from "./pages/ParcoursDetail";
import Manifeste from "./pages/Manifeste";
import Scene from "./pages/Scene";
import Guides from "./pages/Guides";
import GuideDetail from "./pages/GuideDetail";
import GuideMerci from "./pages/GuideMerci";
import Club from "./pages/Club";
import ClubBienvenue from "./pages/ClubBienvenue";
import Loger from "./pages/Loger";
import CoteSecrete from "./pages/CoteSecrete";
import Concierge from "./pages/Concierge";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import SoumettreEvenement from "./pages/SoumettreEvenement";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminPhotos from "./pages/AdminPhotos";
import NotFound from "./pages/NotFound";
import PageTransition from "./components/PageTransition";
import { useFlags } from "./lib/flags";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

function PublicLayout({ children }) {
    return (
        <>
            <Header />
            <main>{children}</main>
            <Footer />
            <PageTransition>{null}</PageTransition>
        </>
    );
}

/** Render children only if the corresponding feature flag is enabled. Otherwise redirect to /. */
function FlaggedRoute({ flag, children }) {
    const { flags, loading } = useFlags();
    if (loading) return null;
    if (flags[flag] === false) return <Navigate to="/" replace />;
    return children;
}

function App() {
    return (
        <HelmetProvider>
            <div className="App">
                <AuthProvider>
                    <FlagsProvider>
                        <BrowserRouter>
                        <ScrollToTop />
                        <Routes>
                            {/* Admin (no header/footer) */}
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminDashboard />} />
                            <Route path="/admin/photos" element={<AdminPhotos />} />

                            {/* Public routes */}
                            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                            <Route path="/destinations" element={<PublicLayout><Destinations /></PublicLayout>} />
                            <Route path="/destinations/:slug" element={<PublicLayout><DestinationDetail /></PublicLayout>} />
                            <Route path="/adresses" element={<PublicLayout><Adresses /></PublicLayout>} />
                            <Route path="/adresses/:slug" element={<PublicLayout><AdresseDetail /></PublicLayout>} />
                            <Route path="/galerie" element={<PublicLayout><Galerie /></PublicLayout>} />
                            <Route path="/gallery" element={<PublicLayout><Galerie /></PublicLayout>} />
                            <Route path="/evenements" element={<PublicLayout><Evenements /></PublicLayout>} />
                            <Route path="/evenements/:slug" element={<PublicLayout><EvenementDetail /></PublicLayout>} />
                            <Route path="/balades" element={<PublicLayout><Balades /></PublicLayout>} />
                            <Route path="/balades/:slug" element={<PublicLayout><BaladeDetail /></PublicLayout>} />
                            <Route path="/magazine" element={<PublicLayout><Magazine /></PublicLayout>} />
                            <Route path="/magazine/:slug" element={<PublicLayout><ArticleDetail /></PublicLayout>} />
                            <Route path="/carte" element={<PublicLayout><Carte /></PublicLayout>} />
                            <Route path="/experiences" element={<PublicLayout><Experiences /></PublicLayout>} />
                            <Route path="/experiences/:slug" element={<PublicLayout><ExperienceDetail /></PublicLayout>} />
                            <Route path="/parcours" element={<PublicLayout><Parcours /></PublicLayout>} />
                            <Route path="/parcours/:slug" element={<PublicLayout><ParcoursDetail /></PublicLayout>} />
                            <Route path="/week-end" element={<PublicLayout><WeekendBuilder /></PublicLayout>} />
                            <Route path="/festival" element={<FlaggedRoute flag="festival"><PublicLayout><Festival /></PublicLayout></FlaggedRoute>} />
                            <Route path="/manifeste" element={<PublicLayout><Manifeste /></PublicLayout>} />
                            <Route path="/scene" element={<PublicLayout><Scene /></PublicLayout>} />
                            <Route path="/guides" element={<FlaggedRoute flag="guides"><PublicLayout><Guides /></PublicLayout></FlaggedRoute>} />
                            <Route path="/guides/:slug" element={<FlaggedRoute flag="guides"><PublicLayout><GuideDetail /></PublicLayout></FlaggedRoute>} />
                            <Route path="/guides/:slug/merci" element={<FlaggedRoute flag="guides"><PublicLayout><GuideMerci /></PublicLayout></FlaggedRoute>} />
                            <Route path="/club" element={<FlaggedRoute flag="club"><PublicLayout><Club /></PublicLayout></FlaggedRoute>} />
                            <Route path="/club/bienvenue" element={<FlaggedRoute flag="club"><PublicLayout><ClubBienvenue /></PublicLayout></FlaggedRoute>} />
                            <Route path="/loger" element={<PublicLayout><Loger /></PublicLayout>} />
                            <Route path="/cote-secrete" element={<FlaggedRoute flag="cote-secrete"><PublicLayout><CoteSecrete /></PublicLayout></FlaggedRoute>} />
                            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                            <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
                            <Route path="/soumettre-evenement" element={<PublicLayout><SoumettreEvenement /></PublicLayout>} />
                            <Route path="/concierge" element={<Navigate to="/" replace />} />

                            <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
                        </Routes>
                    </BrowserRouter>
                </FlagsProvider>
            </AuthProvider>
            </div>
        </HelmetProvider>
    );
}

export default App;
